import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, Pressable, ScrollView } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';

import SheetModal from '@/components/General/SheetModal';
import SolidButton from '@/components/General/SolidButton';
import Unicon from '@/components/General/Unicon';
import Callout from '@/components/General/Callout';
import Avatar from '@/components/People/Avatar';

/**
 * Second step of group event registration: after people are picked in the
 * SheetSelector, choose who (if anyone) receives the confirmation email. The
 * QR code in the email checks in the whole group, so only one recipient is
 * needed. Confirming calls onRegister with the chosen personID (or null for
 * "don't send") — the parent runs the actual register-for-event mutation.
 *
 * @param {boolean} isOpen
 * @param {Function} onClose - Cancel/close without registering.
 * @param {Array} people - The people just selected for registration (raw person records).
 * @param {Function} onRegister - Called with emailRecipientPersonID (string) or null.
 * @param {boolean} processing - Disables confirm while the registration mutation runs.
 */
const RegistrationEmailModal = ({ isOpen, onClose, people = [], onRegister, processing = false }) => {
    const [selectedPersonID, setSelectedPersonID] = useState(null);

    const hasAnyEmail = useMemo(() => (people || []).some(p => p?.email), [people]);

    // Reset selection each open: default to the first person that has an
    // email, or "don't send" (null) when nobody does.
    useEffect(() => {
        if (isOpen) {
            setSelectedPersonID((people || []).find(p => p?.email)?.id ?? null);
        }
    }, [isOpen, people]);

    const personName = (person) => `${person?.firstName || ''} ${person?.lastName || ''}`.trim() || 'Unnamed';

    const radio = (isSelected) => (
        <View style={[styles.radio, isSelected && styles.radioSelected]}>
            {isSelected && <View style={styles.radioDot} />}
        </View>
    );

    return (
        <SheetModal
            isOpen={isOpen}
            onCancel={onClose}
            onClose={onClose}
            title="Send Registration Email"
            subtitle="The confirmation email includes a QR code that works for everyone in this group."
            cancelText="Cancel"
            loading={processing}
        >
            <View style={styles.container} testID="registration-email-modal">
                {!hasAnyEmail && (
                    <Callout
                        type="warning"
                        message="None of the selected people have an email on file — no confirmation can be sent."
                    />
                )}
                <ScrollView style={styles.list} contentContainerStyle={styles.listContent} keyboardShouldPersistTaps="handled">
                    {(people || []).map(person => {
                        const hasEmail = !!person?.email;
                        const isSelected = hasEmail && selectedPersonID === person.id;
                        return (
                            <Pressable
                                key={person.id}
                                disabled={!hasEmail}
                                onPress={() => setSelectedPersonID(person.id)}
                                style={[styles.row, isSelected && styles.rowSelected, !hasEmail && styles.rowDisabled]}
                                testID={`registration-email-option-${person.id}`}
                            >
                                <Avatar size={38} profile={person} disableBorder />
                                <View style={styles.rowMain}>
                                    <Text style={styles.rowName} numberOfLines={1}>{personName(person)}</Text>
                                    <Text style={[styles.rowEmail, !hasEmail && styles.rowNoEmail]} numberOfLines={1}>
                                        {hasEmail ? person.email : 'No email on file'}
                                    </Text>
                                </View>
                                {radio(isSelected)}
                            </Pressable>
                        );
                    })}

                    <Pressable
                        onPress={() => setSelectedPersonID(null)}
                        style={[styles.row, selectedPersonID === null && styles.rowSelected]}
                        testID="registration-email-skip"
                    >
                        <View style={styles.skipIcon}>
                            <Unicon name="email-off-outline" library="material" size={18} colorVariant="textSecondary" />
                        </View>
                        <View style={styles.rowMain}>
                            <Text style={styles.rowName}>Don&apos;t send email</Text>
                            <Text style={styles.rowEmail}>Register without a confirmation email</Text>
                        </View>
                        {radio(selectedPersonID === null)}
                    </Pressable>
                </ScrollView>

                <SolidButton
                    text={(people || []).length > 1 ? `Register ${people.length} People` : 'Register'}
                    type="primary"
                    size="md"
                    processing={processing}
                    disabled={processing}
                    onPress={() => onRegister?.(selectedPersonID)}
                    testID="registration-email-confirm"
                />
            </View>
        </SheetModal>
    );
};

const styles = StyleSheet.create(({ colors, fonts }) => ({
    container: {
        padding: 16,
        gap: 12,
    },
    list: {
        maxHeight: 420,
    },
    listContent: {
        gap: 8,
        paddingBottom: 8,
    },
    row: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        backgroundColor: colors.surface,
        borderRadius: 12,
        padding: 14,
    },
    rowSelected: {
        backgroundColor: colors.primaryContainer,
    },
    rowDisabled: {
        opacity: 0.5,
    },
    rowMain: {
        flex: 1,
        gap: 2,
    },
    rowName: {
        fontSize: 15,
        fontFamily: fonts.heading,
        color: colors.text,
    },
    rowEmail: {
        fontSize: 13,
        fontFamily: fonts.body,
        color: colors.textSecondary,
    },
    rowNoEmail: {
        fontStyle: 'italic',
    },
    skipIcon: {
        width: 38,
        height: 38,
        borderRadius: 19,
        backgroundColor: colors.inputBackground,
        alignItems: 'center',
        justifyContent: 'center',
    },
    radio: {
        width: 22,
        height: 22,
        borderRadius: 11,
        borderWidth: 2,
        borderColor: colors.border,
        justifyContent: 'center',
        alignItems: 'center',
    },
    radioSelected: {
        borderColor: colors.primary,
    },
    radioDot: {
        width: 10,
        height: 10,
        borderRadius: 5,
        backgroundColor: colors.primary,
    },
}));

export default RegistrationEmailModal;
